import { createContext, useContext, useEffect, useState } from 'react'
import { matchPath, useLocation } from 'react-router-dom'

import { clearSelectedEnrollment as clearSelectedEnrollmentApi } from '../../api/enrollments/clear-selected-enrollment'
import { setSelectedEnrollment as setSelectedEnrollmentApi } from '../../api/enrollments/set-selected-enrollment'
import { Paths } from '../constants/path'
import useAuth from '../hooks/use-auth'
import { RoleEnum } from '../types/enums/users/role.enum'

type SelectedEnrollmentContextType = {
  selectedEnrollmentId?: number
  isLoading: boolean
  setSelectedEnrollmentId: (id: number) => Promise<void>
  clearSelectedEnrollment: () => Promise<void>
}

const SelectedEnrollmentContext = createContext<SelectedEnrollmentContextType | undefined>(
  undefined
)

export const SelectedEnrollmentProvider = ({ children }: { children: React.ReactNode }) => {
  const [isLoading, setIsLoading] = useState(true)
  const [selectedEnrollmentId, setSelectedEnrollmentIdState] = useState<number>()
  const { user } = useAuth()
  const location = useLocation()

  useEffect(() => {
    if (user?.role !== RoleEnum.CLIENT) {
      setSelectedEnrollmentIdState(undefined)
      setIsLoading(false)
      return
    }

    const match = matchPath(Paths.CLIENT_SPACE_LAYOUT, location.pathname)
    const id = Number(match?.params.id)
    setSelectedEnrollmentIdState(Number.isInteger(id) ? id : undefined)
    setIsLoading(false)
  }, [location.pathname, user?.role])

  const setSelectedEnrollmentId = async (id: number) => {
    await setSelectedEnrollmentApi(id)
    setSelectedEnrollmentIdState(id)
  }

  const clearSelectedEnrollment = async () => {
    await clearSelectedEnrollmentApi()
    setSelectedEnrollmentIdState(undefined)
  }

  return (
    <SelectedEnrollmentContext.Provider
      value={{
        selectedEnrollmentId,
        setSelectedEnrollmentId,
        clearSelectedEnrollment,
        isLoading
      }}
    >
      {children}
    </SelectedEnrollmentContext.Provider>
  )
}

export const useSelectedEnrollment = () => {
  const context = useContext(SelectedEnrollmentContext)
  if (!context)
    throw new Error('useSelectedEnrollment must be used within a SelectedEnrollmentProvider')
  return context
}
